import { useState, useEffect, useMemo } from "react";
import { useLanguage } from "../i18n";
import { buildFAQSchema } from "../seo/structured-data";

const FAQ_SPLIT_INDEX = 6;

const FAQItem = ({
  question,
  answer,
  isOpen,
  onToggle
}: {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}) => (
  <div className={`faq-card ${isOpen ? "open" : ""}`} onClick={onToggle}>
    <div className="faq-header">
      <h3>{question}</h3>
      <span className="faq-icon">{isOpen ? "−" : "+"}</span>
    </div>
    {isOpen && <div className="faq-answer"><p>{answer}</p></div>}
  </div>
);

const InFeedAd = (): JSX.Element => {
  useEffect(() => {
    try {
      ((window as any).adsbygoogle = (window as any).adsbygoogle || []).push({});
    } catch (_) { /* ad blocker */ }
  }, []);

  return (
    <div className="faq-card faq-ad-card">
      <ins
        className="adsbygoogle"
        style={{ display: "block" }}
        data-ad-format="fluid"
        data-ad-layout-key="-fb+5w+4e-db+86"
        data-ad-client="ca-pub-2031491346868899"
      />
    </div>
  );
};

export const FAQSection = (): JSX.Element => {
  const { t } = useLanguage();
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqItems = t.faq.items;

  // Inject FAQPage structured data (JSON-LD) for search engine rich results
  const faqSchema = useMemo(() => buildFAQSchema(faqItems), [faqItems]);

  useEffect(() => {
    let script = document.querySelector('script[data-seo="faq-json-ld"]') as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.dataset.seo = "faq-json-ld";
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(faqSchema);

    return () => {
      document.querySelector('script[data-seo="faq-json-ld"]')?.remove();
    };
  }, [faqSchema]);

  const toggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  // Split FAQ items to insert ad in middle
  const firstHalf = faqItems.slice(0, FAQ_SPLIT_INDEX);
  const secondHalf = faqItems.slice(FAQ_SPLIT_INDEX);

  return (
    <section id="faq" className="faq-section">
      <div className="box" style={{ background: "var(--accent-tertiary)", color: "#fff" }}>
        <h2 style={{ fontSize: "2.5rem", marginBottom: "1rem", textTransform: "uppercase" }}>
          {t.faq.title.split(" ").slice(0, 2).join(" ")} <br /> {t.faq.title.split(" ").slice(2).join(" ")}
        </h2>
        <p style={{ color: "#eee", fontSize: "1.1rem", marginBottom: "2rem" }}>
          {t.faq.subtitle}
        </p>

        <div className="faq-stack">
          {firstHalf.map((item, index) => (
            <FAQItem
              key={index}
              question={item.question}
              answer={item.answer}
              isOpen={openIndex === index}
              onToggle={() => toggle(index)}
            />
          ))}
          
          <InFeedAd />
          
          {secondHalf.map((item, index) => (
            <FAQItem
              key={index + FAQ_SPLIT_INDEX}
              question={item.question}
              answer={item.answer}
              isOpen={openIndex === index + FAQ_SPLIT_INDEX}
              onToggle={() => toggle(index + FAQ_SPLIT_INDEX)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

